import { FC, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useQueryClient } from '@tanstack/react-query';
import {
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import {
  createBemBlockBuilder,
  InPageNotification,
  Loading,
} from '@ebsco-ui/ebsco-ui';

import {
  GuestUserModalState,
  Modal,
  ModalHeader,
  Notification,
  Toast,
} from '@app/components';
import { useAppContext } from '@app/contexts';
import {
  ProjectCreationDto,
  queryKeys,
  useCreateProjectsMutation,
} from '@app/hooks';
import { sharedMessages } from '@app/translations';

import { ProjectModalForm } from './ProjectModalForm';

import './ProjectModal.scss';

interface CreateNewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const cnBem = createBemBlockBuilder(['projectModal']);

export const CreateNewProjectModal: FC<CreateNewProjectModalProps> = ({
  isOpen,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const { $t } = useIntl();
  const createProjectMutation = useCreateProjectsMutation();
  const { isLoggedIn } = useAppContext();

  const handleSubmit = (values: ProjectCreationDto) =>
    createProjectMutation.mutate(values);

  useEffect(() => {
    if (createProjectMutation.isSuccess) {
      onClose();
      queryClient.refetchQueries([queryKeys.projects], {
        type: 'active',
      });

      Toast({
        type: 'success',
        icon: faCheckCircle,
        title: $t({
          id: 'projects.projectHasBeenCreated',
          defaultMessage: 'Your project has been created.',
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [$t, createProjectMutation.isSuccess]);

  const handleAfterClose = () => {
    createProjectMutation.reset();
  };

  const renderErrorNotification = () => (
    <Notification
      variant={InPageNotification.VARIANT.error}
      icon={faExclamationTriangle}
      className={cnBem('__notification')}
      header={$t(sharedMessages.somethingWentWrong)}
    />
  );

  const isLoading =
    createProjectMutation.isLoading || createProjectMutation.isSuccess;

  const renderSignInMessage = () => (
    <Modal.Body>
      <GuestUserModalState
        message={$t({
          id: 'projects.signInToCreateProject',
          defaultMessage: 'Please sign in to your account to create a project.',
        })}
      />
    </Modal.Body>
  );

  const renderBody = () => {
    if (!isLoggedIn) {
      return renderSignInMessage();
    }

    if (isLoading) {
      return (
        <Modal.Body>
          <Loading loadingMessage={$t(sharedMessages.loadingMessage)} />
        </Modal.Body>
      );
    }

    return (
      <ProjectModalForm onSubmit={handleSubmit} onClose={onClose}>
        {createProjectMutation.isError && renderErrorNotification()}
      </ProjectModalForm>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      modalTitle={$t(sharedMessages.createNewProject)}
      className={cnBem()}
      onClose={onClose}
      onAfterClose={handleAfterClose}
    >
      <ModalHeader
        title={$t(sharedMessages.createNewProject)}
        className={cnBem('__header')}
        onClose={onClose}
      />
      {renderBody()}
    </Modal>
  );
};
